import type { ActorEvent } from '@lighthouse/core'
import {
    type FieldADTValue,
    type HighLightConditions,
    type RecordLikeProtocol,
    type RichTextContentProtocol,
    type SelectedMode,
    type TableColumnWidth,
    type ViewField
} from '@lighthouse/core'
import type { FetchPexelsParams, PexelsResponse } from '@lighthouse/shared'
import { getPrimaryDataSourceEnableFieldIds, useApplicationContext, type UseUploadFileSParameter } from '@lighthouse/shared'
import type { UploadyProps } from '@rpldy/uploady'
import { equals, sortBy } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { AdvancedTableContent } from './AdvancedTableContent'
import AdvancedTableHeader from './AdvancedTableHeader'

interface TableProps {
    checkable?: boolean
    viewFields: ViewField[]
    selectedRecords?: string[]
    highLightRules?: HighLightConditions
    scrollRef: React.RefObject<HTMLDivElement>
    blockWidth?: number
    uploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    richTextUploadOptions: UploadyProps
    videoUploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    onRecordSelect?: (recordIds: string[]) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onCellUpdate?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onSelectModeChange?: (mode?: SelectedMode) => void
    onTableColumnWidthChange: (val: TableColumnWidth) => void
    onFetchPexelsList: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

const SCxTableContainer = styled.div`
    height: 100%;
    width: 100%;
    overflow: visible;
    background-color: #ffffff;
`

export const Table: React.FC<TableProps> = ({
    checkable = true,
    viewFields,
    selectedRecords,
    highLightRules,
    scrollRef,
    blockWidth,
    uploadOptions,
    richTextUploadOptions,
    videoUploadOptions,
    onRecordSelect,
    onCellChange,
    onCellUpdate,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle,
    onSelectModeChange,
    onTableColumnWidthChange,
    onFetchPexelsList
}) => {
    const { blockData, recordPoints, dataSource } = useViewBlockContext()
    const { id: blockId } = blockData
    const { isMobile } = useApplicationContext()
    const noRecords = recordPoints.length === 0
    const { schema, viewOptions, type } = dataSource || {}

    const primaryDataSourceFieldIds = useMemo(() => {
        if (!schema || !viewOptions || !type) {
            return
        }
        return getPrimaryDataSourceEnableFieldIds(schema, viewOptions, type)
    }, [schema, viewOptions, type])
    const allRecordSelected = useMemo(() => {
        return (
            selectedRecords &&
            !noRecords &&
            equals(
                sortBy(a => a, selectedRecords),
                sortBy(
                    a => a,
                    recordPoints.map(p => p.id)
                )
            )
        )
    }, [noRecords, recordPoints, selectedRecords])

    const handleAllRecordSelect = useCallback(
        (selected: boolean) => {
            if (selected) {
                onRecordSelect?.(recordPoints.map(p => p.id))
            } else {
                onRecordSelect?.([])
            }
        },
        [onRecordSelect, recordPoints]
    )

    // const handleChangeTableProps = useCallback((columns: ViewField[]) => {
    //     setColumns(columns)
    // }, [])

    return (
        <SCxTableContainer className="tableContainer">
            <AdvancedTableHeader
                id={blockId}
                checkable={checkable}
                viewFields={viewFields}
                allRecordSelected={allRecordSelected}
                isMobile={isMobile}
                onAllRecordSelect={handleAllRecordSelect}
                onSelectModeChange={onSelectModeChange}
                onTableColumnWidthChange={onTableColumnWidthChange}
            />
            {!!blockWidth && (
                <AdvancedTableContent
                    checkable={checkable}
                    viewFields={viewFields}
                    blockWidth={blockWidth}
                    scrollRef={scrollRef}
                    selectedRecords={selectedRecords}
                    highLightRules={highLightRules}
                    primaryDataSourceFieldIds={primaryDataSourceFieldIds}
                    uploadOptions={uploadOptions}
                    richTextUploadOptions={richTextUploadOptions}
                    videoUploadOptions={videoUploadOptions}
                    onRecordSelect={onRecordSelect}
                    onCellChange={onCellChange}
                    onCellUpdate={onCellUpdate}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderButtonTitle}
                    onFetchPexelsList={onFetchPexelsList}
                />
            )}
        </SCxTableContainer>
    )
}
